import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  LoginCredentials,
  AuthSession,
  AccessTokenResponse,
} from '../../types/UserInterface';
import { extractUserFromToken, isTokenExpired } from '../../utils/jwt';
import { CilpronApi } from '../../constant/Banner';

const { BASE_URL, TOKEN_KEY, TENANT_TOKEN_KEY, API_KEY, ENDPOINTS } = CilpronApi;

export const loginThunk = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {

      // ── Step 1: Login → get JWT ──────────────────────────────
      const loginResponse = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain',
        },
        body: JSON.stringify({
          email:    credentials.email,
          password: credentials.password,
        }),
      });

      if (!loginResponse.ok) {
        const text = await loginResponse.text();
        try {
          const err = JSON.parse(text);
          return rejectWithValue(err.message ?? 'Invalid email or password');
        } catch {
          return rejectWithValue(text || 'Invalid email or password');
        }
      }

      const loginToken = await loginResponse.text();

      if (!loginToken || loginToken.split('.').length !== 3) {
        return rejectWithValue('Invalid token received from server');
      }

      // ── Step 2: Decode JWT → extract user + userId ───────────
      const user = extractUserFromToken(loginToken);

      if (!user?.userId) {
        return rejectWithValue('Could not extract user from token');
      }

      // ── Step 3: Fetch tenant access tokens ───────────────────
      const tokenResponse = await fetch(
        `${BASE_URL}${ENDPOINTS.ACCESS_TOKEN(user.userId)}`,
        {
          method: 'GET',
          headers: {
            'accept':        'application/json',
            'Authorization': `Bearer ${loginToken}`,
          },
        }
      );

      if (!tokenResponse.ok) {
        return rejectWithValue('Failed to load access tokens');
      }

      const tokenData: AccessTokenResponse = await tokenResponse.json();
      const tokens = tokenData.$values ?? [];

      // ✅ ApiKey — used for direct API calls
      const apiKeyEntry = tokens.find((t) => t.scope === 'ApiKey');
      const apiKey      = apiKeyEntry?.token ?? '';

      // ✅ Tenant JWT — if a separate one exists use it,
      //    otherwise the loginToken IS the tenant JWT (your API's design)
      const tenantTokenEntry = tokens.find(
        (t) => t.scope === null && t.token.split('.').length === 3
      );
      const tenantToken = tenantTokenEntry?.token ?? loginToken;

      // ── Step 4: Persist all tokens ────────────────────────────
      localStorage.setItem(TOKEN_KEY,        loginToken);
      localStorage.setItem(TENANT_TOKEN_KEY, tenantToken);
      localStorage.setItem(API_KEY,          apiKey);

      const session: AuthSession = {
        user,
        loginToken,
        tenantToken,
        apiKey,
      };

      return session;

    } catch {
      return rejectWithValue('Network error — please check your connection');
    }
  }
);

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TENANT_TOKEN_KEY);
    localStorage.removeItem(API_KEY);
  }
);

export const refreshSessionThunk = createAsyncThunk(
  'auth/refreshSession',
  async (_arg: void, { rejectWithValue }) => {
    const loginToken  = localStorage.getItem(TOKEN_KEY);
    const tenantToken = localStorage.getItem(TENANT_TOKEN_KEY);
    const apiKey      = localStorage.getItem(API_KEY);

    if (!loginToken || !tenantToken) {
      return rejectWithValue('No session found');
    }

    if (isTokenExpired(loginToken)) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_TOKEN_KEY);
      localStorage.removeItem(API_KEY);
      return rejectWithValue('Session expired — please sign in again');
    }

    const user = extractUserFromToken(loginToken);

    if (!user) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_TOKEN_KEY);
      localStorage.removeItem(API_KEY);
      return rejectWithValue('Invalid session');
    }

    return {
      user,
      loginToken,
      tenantToken,
      apiKey: apiKey ?? '',
    } as AuthSession;
  }
);